<script setup lang="ts">
/**
 * Interactive tutorials — the Completion card (handoff README §6).
 *
 * The checklist is the tour's own step names rather than a second hardcoded
 * list, phrased in the past tense by the registry order: a second tutorial gets
 * its completion card for free, and the ticks can never disagree with the pills
 * above them.
 */

import { computed } from 'vue'
import TourModal from './TourModal.vue'
import { renderMarkdownLite } from './engine'
import type { Tour } from './registry'

const props = defineProps<{ tour: Tour }>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'events'): void
}>()

/** Past-tense phrasing for the checklist; falls back to the step's own name. */
const DONE_LABELS: Record<string, string> = {
  'Create event': 'Created an event',
  'Pricing details': 'Set a pricing basis',
  Arrangements: 'Costed an arrangement',
  'Send for e-signature': 'Sent it for signature',
}

const checklist = computed(() => props.tour.steps.map((s) => DONE_LABELS[s] ?? s))

const bodyHtml = computed(() =>
  renderMarkdownLite('You can redo this any time from **Tutorials** in your profile menu.')
)
</script>

<template>
  <TourModal
    eyebrow="Tutorial complete"
    title="Your first quote is out the door"
    :steps="tour.steps"
    :current-step="tour.steps.length"
  >
    <ul class="tour-check">
      <li v-for="label in checklist" :key="label">{{ label }}</li>
    </ul>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <p class="tour-modal__body" v-html="bodyHtml" />

    <template #foot>
      <button type="button" class="bz-btn bz-btn-primary" @click="emit('close')">
        Back to the event
      </button>
      <button type="button" class="tour-link" @click="emit('events')">Go to Events</button>
    </template>
  </TourModal>
</template>
